/**
 * Find the drawing on a sheet image (docs/DRB_PRESENTATION.md "Zoom to the drawing").
 *
 * PURE: takes a grayscale raster of the sheet as rendered (top-left origin) and returns a crop as
 * page-relative visual fractions [left, top, right, bottom] that frames the drawing, shaped to the
 * slide's drawing area so it fills it. Never reads a PDF itself — the caller renders the page.
 *
 * How: ink pixels inside the search area, thickened a little so a drawing's lines join into blobs,
 * then split into connected pieces. Sheet furniture is dropped — thin pieces running along the
 * search area's edge (the sheet border, grid ticks) and specks far smaller than the drawing (stray
 * reference numbers) — and the crop is the box around what's left, padded and widened or heightened
 * to the slide's shape.
 */

/** The firm's 36x24 sheet: drawing area left of the notes column and title strip (fractions). */
export const AJ_DRAWING_AREA = [0.034, 0.018, 0.797, 0.985]
/** Width / height of a drawing slide's drawing area (api/_drbDeck.js drawingSlide: 612 x 500). */
export const SLIDE_DRAWING_ASPECT = 612 / 500

/**
 * @param {Uint8Array|Uint8ClampedArray} gray  one byte per pixel, 0 = black
 * @param {number} w
 * @param {number} h
 * @param {object} [opts]
 *   area:   [l,t,r,b] fractions to search inside (default: whole page)
 *   aspect: crop width / height on the PAGE in points (default SLIDE_DRAWING_ASPECT); pass
 *           null to keep the drawing's own shape
 *   pageAspect: page width / height in points (default w/h)
 * @returns {[number, number, number, number] | null}
 */
export function findDrawingCrop(gray, w, h, opts = {}) {
  const [al, at, ar, ab] = opts.area || [0, 0, 1, 1]
  const x0 = Math.max(0, Math.floor(al * w)), x1 = Math.min(w, Math.ceil(ar * w))
  const y0 = Math.max(0, Math.floor(at * h)), y1 = Math.min(h, Math.ceil(ab * h))
  const W = x1 - x0, H = y1 - y0
  if (W < 20 || H < 20) return null

  // 1. ink, thickened by R so nearby strokes join
  const R = Math.max(2, Math.round(Math.min(W, H) / 200))
  const ink = new Uint8Array(W * H)
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (gray[(y + y0) * w + x + x0] < 200) ink[y * W + x] = 1
  // long straight lines are sheet furniture (the border, the notes-column divider) — and they would
  // join everything they touch into one piece. A drawing keeps its extent through what's left
  // (grid bubbles, dimensions, hatching), so erasing its own long gridlines is harmless.
  eraseLongRuns(ink, W, H, Math.round(W * 0.45), true)
  eraseLongRuns(ink, W, H, Math.round(H * 0.45), false)
  const rowGrow = new Uint8Array(W * H)
  for (let y = 0; y < H; y++) {
    let last = -1e9
    for (let x = 0; x < W; x++) { if (ink[y * W + x]) last = x; if (x - last <= R) rowGrow[y * W + x] = 1 }
    last = 1e9
    for (let x = W - 1; x >= 0; x--) { if (ink[y * W + x]) last = x; if (last - x <= R) rowGrow[y * W + x] = 1 }
  }
  const mask = new Uint8Array(W * H)
  for (let x = 0; x < W; x++) {
    let last = -1e9
    for (let y = 0; y < H; y++) { if (rowGrow[y * W + x]) last = y; if (y - last <= R) mask[y * W + x] = 1 }
    last = 1e9
    for (let y = H - 1; y >= 0; y--) { if (rowGrow[y * W + x]) last = y; if (last - y <= R) mask[y * W + x] = 1 }
  }

  // 2. connected pieces (4-neighbour, iterative)
  const label = new Int32Array(W * H)
  const pieces = []
  const stack = new Int32Array(W * H)
  for (let i = 0; i < W * H; i++) {
    if (!mask[i] || label[i]) continue
    const id = pieces.length + 1
    let sp = 0, count = 0, inkCount = 0
    let mnx = W, mny = H, mxx = -1, mxy = -1
    stack[sp++] = i; label[i] = id
    while (sp) {
      const k = stack[--sp]
      const x = k % W, y = (k - x) / W
      count++; if (ink[k]) inkCount++
      if (x < mnx) mnx = x; if (x > mxx) mxx = x; if (y < mny) mny = y; if (y > mxy) mxy = y
      if (x > 0 && mask[k - 1] && !label[k - 1]) { label[k - 1] = id; stack[sp++] = k - 1 }
      if (x < W - 1 && mask[k + 1] && !label[k + 1]) { label[k + 1] = id; stack[sp++] = k + 1 }
      if (y > 0 && mask[k - W] && !label[k - W]) { label[k - W] = id; stack[sp++] = k - W }
      if (y < H - 1 && mask[k + W] && !label[k + W]) { label[k + W] = id; stack[sp++] = k + W }
    }
    pieces.push({ count, inkCount, mnx, mny, mxx, mxy })
  }

  // 3. drop sheet furniture
  const edge = Math.max(3, Math.round(Math.min(W, H) * 0.015))
  const thin = Math.min(W, H) * 0.04
  const kept = pieces.filter((p) => {
    const bw = p.mxx - p.mnx + 1, bh = p.mxy - p.mny + 1
    const touches = p.mnx <= edge || p.mny <= edge || p.mxx >= W - 1 - edge || p.mxy >= H - 1 - edge
    if (touches && Math.min(bw, bh) < thin) return false                 // border line, edge ticks
    if (bw > W * 0.9 && bh > H * 0.9 && p.count < W * H * 0.08) return false   // an outline frame around everything
    return true
  })
  if (!kept.length) return null
  // the drawing is the biggest piece plus whatever sits close around it (labels, dimensions, the
  // street name); a notes table or schedule placed off to the side stays out
  kept.sort((a, b) => b.inkCount - a.inkCount)
  const seed = kept[0]
  let l = seed.mnx, t = seed.mny, r = seed.mxx + 1, b = seed.mxy + 1
  const reach = Math.max(W, H) * 0.05
  const used = new Set([0])
  for (let grew = true; grew;) {
    grew = false
    kept.forEach((p, i) => {
      if (used.has(i) || p.inkCount < seed.inkCount * 0.0005) return
      const gx = Math.max(0, p.mnx - r, l - p.mxx), gy = Math.max(0, p.mny - b, t - p.mxy)
      if (gx > reach || gy > reach) return
      // a big block touching only a corner of the drawing is a table beside it, not part of it
      const bw = p.mxx - p.mnx + 1, bh = p.mxy - p.mny + 1
      if (gx > 0 && gy > 0) return
      used.add(i); grew = true
      l = Math.min(l, p.mnx); t = Math.min(t, p.mny); r = Math.max(r, p.mxx + 1); b = Math.max(b, p.mxy + 1)
      void bw; void bh
    })
  }

  // 4. pad, shape to the slide, keep inside the search area
  const pad = Math.max(r - l, b - t) * 0.03
  l -= pad; t -= pad; r += pad; b += pad
  const pageAspect = opts.pageAspect || w / h
  const aspect = opts.aspect === undefined ? SLIDE_DRAWING_ASPECT : opts.aspect
  if (aspect) {
    // pixel aspect equals point aspect when the raster keeps the page's proportions
    const pxAspect = aspect * (w / h) / pageAspect
    const cw = r - l, ch = b - t
    if (cw / ch < pxAspect) { const nw = ch * pxAspect; l -= (nw - cw) / 2; r = l + nw }
    else { const nh = cw / pxAspect; t -= (nh - ch) / 2; b = t + nh }
    // slide inside the area, shrinking only if it can't fit
    const fit = (lo, hi, min, max) => {
      const span = hi - lo
      if (span >= max - min) return [min, max]
      if (lo < min) return [min, min + span]
      if (hi > max) return [max - span, max]
      return [lo, hi]
    }
    ;[l, r] = fit(l, r, 0, W)
    ;[t, b] = fit(t, b, 0, H)
  } else {
    l = Math.max(0, l); t = Math.max(0, t); r = Math.min(W, r); b = Math.min(H, b)
  }
  const crop = [(l + x0) / w, (t + y0) / h, (r + x0) / w, (b + y0) / h]
  // not worth it when it barely zooms
  const area = (crop[2] - crop[0]) * (crop[3] - crop[1]), full = (ar - al) * (ab - at)
  if (area > full * 0.92) return null
  return crop.map((v) => Math.round(v * 10000) / 10000)
}

/** Clear runs of ink longer than `min` pixels along rows (horizontal) or columns. Gaps of a pixel
 *  are bridged so an anti-aliased line still reads as one run. */
function eraseLongRuns(ink, W, H, min, horizontal) {
  const outer = horizontal ? H : W, inner = horizontal ? W : H
  const at = horizontal ? (o, i) => o * W + i : (o, i) => i * W + o
  for (let o = 0; o < outer; o++) {
    let start = -1, gap = 0
    for (let i = 0; i <= inner; i++) {
      const on = i < inner && ink[at(o, i)]
      if (on) { if (start < 0) start = i; gap = 0; continue }
      if (start >= 0 && i < inner && gap < 1) { gap++; continue }
      if (start >= 0) {
        const end = i - gap
        if (end - start >= min) for (let k = start; k < end; k++) { ink[at(o, k)] = 0; if (horizontal) { if (o > 0) ink[at(o - 1, k)] = 0; if (o < outer - 1) ink[at(o + 1, k)] = 0 } else { if (o > 0) ink[at(o - 1, k)] = 0; if (o < outer - 1) ink[at(o + 1, k)] = 0 } }
      }
      start = -1; gap = 0
    }
  }
}
